import { useState, useRef, useCallback } from 'react'
import { Upload, X, ScanLine, Check, AlertCircle, Trash2, ChevronDown, ChevronUp } from 'lucide-react'
import { Modal } from '@/components/ui/Modal'
import { Button } from '@/components/ui/Button'
import { toast } from 'sonner'
import { useScanMenuPhotos, useBulkImportMenu } from '@/lib/queries/usePOS'
import { usePOSCurrency } from '@/lib/pos/currency'
import { POSVisualKeyboardField, POSVisualNumpadField } from './POSVisualInput'
import type { ScannedCategory, ScannedItem } from '@/lib/queries/usePOS'

// ─── Types ────────────────────────────────────────────────────────────────────

interface ReviewItem extends ScannedItem {
  included: boolean
}

interface ReviewCategory {
  name: string
  items: ReviewItem[]
  collapsed: boolean
}

// ─── Image upload drop zone ───────────────────────────────────────────────────

function DropZone({
  images,
  onAdd,
  onRemove,
}: {
  images: { preview: string; base64: string }[]
  onAdd: (files: File[]) => void
  onRemove: (index: number) => void
}) {
  const inputRef = useRef<HTMLInputElement>(null)
  const [dragging, setDragging] = useState(false)

  const processFiles = (files: File[]) => {
    const imageFiles = files.filter((f) => f.type.startsWith('image/'))
    if (imageFiles.length === 0) return
    if (images.length + imageFiles.length > 8) {
      toast.error('Maximum 8 images')
      return
    }
    onAdd(imageFiles)
  }

  const onDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault()
      setDragging(false)
      processFiles(Array.from(e.dataTransfer.files))
    },
    [images.length],
  )

  return (
    <div className="space-y-3">
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        multiple
        className="hidden"
        onChange={(e) => processFiles(Array.from(e.target.files ?? []))}
      />

      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); setDragging(true) }}
        onDragLeave={() => setDragging(false)}
        onDrop={onDrop}
        className={`w-full flex flex-col items-center justify-center gap-2 h-32 rounded-xl border-2 border-dashed transition-colors cursor-pointer ${
          dragging
            ? 'border-[var(--color-accent-amber)] bg-[var(--color-accent-amber-dim)]'
            : 'border-[var(--color-border-default)] bg-[var(--color-bg-elevated)] hover:border-[var(--color-accent-amber)]/50'
        }`}
      >
        <Upload className="h-6 w-6 text-[var(--color-text-muted)]" />
        <span className="text-sm text-[var(--color-text-secondary)]">
          Drop photos here or click to upload
        </span>
        <span className="text-xs text-[var(--color-text-muted)]">
          JPG, PNG, WebP — up to 8 pages
        </span>
      </button>

      {images.length > 0 && (
        <div className="grid grid-cols-4 gap-2">
          {images.map((img, i) => (
            <div key={i} className="relative group rounded-lg overflow-hidden border border-[var(--color-border-subtle)] aspect-[3/4]">
              <img src={img.preview} alt={`Page ${i + 1}`} className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors" />
              <button
                type="button"
                onClick={() => onRemove(i)}
                className="absolute top-1 right-1 h-6 w-6 flex items-center justify-center rounded-full bg-black/60 text-white opacity-0 group-hover:opacity-100 transition-opacity"
              >
                <X className="h-3.5 w-3.5" />
              </button>
              <span className="absolute bottom-1 left-1 text-xs bg-black/50 text-white rounded px-1">
                {i + 1}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

// ─── Review step ──────────────────────────────────────────────────────────────

function ReviewStep({
  categories,
  onChange,
  currency,
}: {
  categories: ReviewCategory[]
  onChange: (updated: ReviewCategory[]) => void
  currency: string
}) {
  const updateItem = (ci: number, ii: number, patch: Partial<ReviewItem>) => {
    const next = categories.map((cat, c) =>
      c !== ci
        ? cat
        : {
            ...cat,
            items: cat.items.map((item, idx) =>
              idx !== ii ? item : { ...item, ...patch },
            ),
          },
    )
    onChange(next)
  }

  const toggleCategory = (ci: number) => {
    onChange(
      categories.map((cat, c) => (c === ci ? { ...cat, collapsed: !cat.collapsed } : cat)),
    )
  }

  const toggleAllInCategory = (ci: number, included: boolean) => {
    onChange(
      categories.map((cat, c) =>
        c !== ci ? cat : { ...cat, items: cat.items.map((item) => ({ ...item, included })) },
      ),
    )
  }

  const totalSelected = categories.reduce(
    (sum, cat) => sum + cat.items.filter((i) => i.included).length,
    0,
  )

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between text-sm">
        <span className="text-[var(--color-text-secondary)]">
          Review extracted items — uncheck any you don't want to import
        </span>
        <span className="font-medium text-[var(--color-text-primary)]">
          {totalSelected} selected
        </span>
      </div>

      <div className="space-y-2 max-h-[420px] overflow-y-auto pr-1">
        {categories.map((cat, ci) => {
          const allChecked = cat.items.every((i) => i.included)
          const someChecked = cat.items.some((i) => i.included)
          return (
            <div
              key={ci}
              className="rounded-xl border border-[var(--color-border-default)] overflow-hidden"
            >
              {/* Category header */}
              <div className="flex items-center gap-3 px-3 py-2.5 bg-[var(--color-bg-elevated)]">
                <input
                  type="checkbox"
                  checked={allChecked}
                  ref={(el) => { if (el) el.indeterminate = someChecked && !allChecked }}
                  onChange={(e) => toggleAllInCategory(ci, e.target.checked)}
                  className="rounded"
                />
                <span className="flex-1 text-sm font-semibold text-[var(--color-text-primary)]">
                  {cat.name}
                </span>
                <span className="text-xs text-[var(--color-text-muted)]">
                  {cat.items.filter((i) => i.included).length}/{cat.items.length}
                </span>
                <button
                  type="button"
                  onClick={() => toggleCategory(ci)}
                  className="text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)]"
                >
                  {cat.collapsed ? (
                    <ChevronDown className="h-4 w-4" />
                  ) : (
                    <ChevronUp className="h-4 w-4" />
                  )}
                </button>
              </div>

              {/* Items */}
              {!cat.collapsed && (
                <div className="divide-y divide-[var(--color-border-subtle)]">
                  {cat.items.map((item, ii) => (
                    <div
                      key={ii}
                      className={`flex items-start gap-3 px-3 py-2.5 transition-colors ${
                        !item.included ? 'opacity-40' : ''
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={item.included}
                        onChange={(e) => updateItem(ci, ii, { included: e.target.checked })}
                        className="mt-0.5 rounded"
                      />
                      <div className="flex-1 min-w-0 space-y-1">
                        <POSVisualKeyboardField
                          value={item.name}
                          onChange={(value) => updateItem(ci, ii, { name: value })}
                          inputClassName="w-full h-8 text-sm font-medium bg-transparent border-b border-transparent hover:border-[var(--color-border-default)] focus:border-[var(--color-accent-amber)]/50 py-0.5"
                        />
                        <POSVisualKeyboardField
                          value={item.description ?? ''}
                          onChange={(value) =>
                            updateItem(ci, ii, {
                              description: value || null,
                            })
                          }
                          placeholder="No description"
                          inputClassName="w-full h-8 text-xs bg-transparent border-b border-transparent hover:border-[var(--color-border-default)] focus:border-[var(--color-accent-amber)]/50 py-0.5"
                        />
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        {item.price === null && (
                          <span title="Price not found">
                            <AlertCircle className="h-3.5 w-3.5 text-amber-500" />
                          </span>
                        )}
                        <POSVisualNumpadField
                          value={item.price == null ? '' : String(item.price)}
                          onChange={(value) =>
                            updateItem(ci, ii, {
                              price: value === '' ? null : parseFloat(value),
                            })
                          }
                          placeholder="—"
                          prefix={currency}
                          className="w-32"
                          inputClassName={`h-8 py-0.5 text-sm ${
                            item.price === null
                              ? 'border-amber-500/50 text-amber-500'
                              : 'text-[var(--color-text-primary)]'
                          }`}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}

// ─── Main Modal ───────────────────────────────────────────────────────────────

type ModalStep = 'upload' | 'scanning' | 'review' | 'done'

export function MenuPhotoImportModal({
  open,
  onClose,
}: {
  open: boolean
  onClose: () => void
}) {
  const currency = usePOSCurrency()
  const [modalStep, setModalStep] = useState<ModalStep>('upload')
  const [images, setImages] = useState<{ preview: string; base64: string }[]>([])
  const [reviewCategories, setReviewCategories] = useState<ReviewCategory[]>([])
  const [importResult, setImportResult] = useState<{
    categoriesCreated: number;
    itemsCreated: number;
    variantsCreated?: number;
    modifiersCreated?: number;
  } | null>(null)

  const scanMutation = useScanMenuPhotos()
  const importMutation = useBulkImportMenu()

  const addImages = (files: File[]) => {
    files.forEach((file) => {
      const reader = new FileReader()
      reader.onload = (e) => {
        const base64 = e.target?.result as string
        setImages((prev) => [...prev, { preview: base64, base64 }])
      }
      reader.readAsDataURL(file)
    })
  }

  const removeImage = (index: number) => {
    setImages((prev) => prev.filter((_, i) => i !== index))
  }

  const handleScan = async () => {
    if (images.length === 0) return
    setModalStep('scanning')
    try {
      const result = await scanMutation.mutateAsync(images.map((i) => i.base64))
      const rawCats: ScannedCategory[] = result.categories ?? []
      if (rawCats.length === 0 || rawCats.every((c) => c.items.length === 0)) {
        toast.error('No menu items could be extracted. Try a clearer photo.')
        setModalStep('upload')
        return
      }
      setReviewCategories(
        rawCats
          .filter((c) => c.items.length > 0)
          .map((cat) => ({
            name: cat.name,
            collapsed: false,
            items: cat.items.map((item) => ({ ...item, included: true })),
          })),
      )
      setModalStep('review')
    } catch (err: any) {
      toast.error(err?.message ?? 'Scan failed')
      setModalStep('upload')
    }
  }

  const handleImport = async () => {
    const toImport: ScannedCategory[] = reviewCategories
      .map((cat) => ({
        name: cat.name,
        items: cat.items
          .filter((i) => i.included && i.name.trim())
          .map(({ name, description, price, orderRole, extraAppliesToCategories, extraAppliesToItems, variants, modifiers, options }) => ({
            name,
            description,
            price,
            orderRole,
            extraAppliesToCategories,
            extraAppliesToItems,
            variants,
            modifiers,
            options,
          })),
      }))
      .filter((cat) => cat.items.length > 0)

    if (toImport.length === 0) {
      toast.error('No items selected to import')
      return
    }

    try {
      const res = await importMutation.mutateAsync(toImport)
      setImportResult(res.data)
      setModalStep('done')
      toast.success(`Imported ${res.data?.itemsCreated ?? 0} menu items`)
    } catch (err: any) {
      toast.error(err?.message ?? 'Import failed')
    }
  }

  const handleClose = () => {
    onClose()
    // Reset after animation
    setTimeout(() => {
      setModalStep('upload')
      setImages([])
      setReviewCategories([])
      setImportResult(null)
    }, 300)
  }

  const totalSelected = reviewCategories.reduce(
    (sum, cat) => sum + cat.items.filter((i) => i.included).length,
    0,
  )

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title={
        modalStep === 'upload'
          ? 'Import menu from photos'
          : modalStep === 'scanning'
          ? 'Scanning menu…'
          : modalStep === 'review'
          ? 'Review extracted items'
          : 'Import complete'
      }
      size="lg"
    >
      {/* UPLOAD */}
      {modalStep === 'upload' && (
        <div className="space-y-5">
          <p className="text-sm text-[var(--color-text-secondary)]">
            Take clear photos of your menu pages. AI will extract categories, dishes, descriptions
            and prices automatically.
          </p>
          <DropZone images={images} onAdd={addImages} onRemove={removeImage} />
          <div className="flex justify-end gap-2 pt-1">
            <Button variant="secondary" onClick={handleClose}>
              Cancel
            </Button>
            <Button onClick={handleScan} disabled={images.length === 0}>
              <ScanLine className="h-4 w-4 mr-1.5" /> Scan with AI
            </Button>
          </div>
        </div>
      )}

      {/* SCANNING */}
      {modalStep === 'scanning' && (
        <div className="flex flex-col items-center justify-center py-12 gap-4">
          <div className="relative">
            <ScanLine className="h-12 w-12 text-[var(--color-accent-amber)] animate-pulse" />
          </div>
          <div className="text-center">
            <p className="font-medium text-[var(--color-text-primary)]">
              Analysing {images.length} photo{images.length !== 1 ? 's' : ''}…
            </p>
            <p className="text-sm text-[var(--color-text-secondary)] mt-1">
              This usually takes 5–15 seconds
            </p>
          </div>
        </div>
      )}

      {/* REVIEW */}
      {modalStep === 'review' && (
        <div className="space-y-5">
          <ReviewStep categories={reviewCategories} onChange={setReviewCategories} currency={currency} />
          <div className="flex items-center justify-between pt-1 border-t border-[var(--color-border-subtle)]">
            <button
              type="button"
              onClick={() => setModalStep('upload')}
              className="text-sm text-[var(--color-text-muted)] hover:text-[var(--color-text-secondary)]"
            >
              ← Back to upload
            </button>
            <div className="flex gap-2">
              <Button variant="secondary" onClick={handleClose}>
                Cancel
              </Button>
              <Button
                onClick={handleImport}
                loading={importMutation.isPending}
                disabled={totalSelected === 0}
              >
                <Check className="h-4 w-4 mr-1.5" />
                Import {totalSelected} item{totalSelected !== 1 ? 's' : ''}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* DONE */}
      {modalStep === 'done' && (
        <div className="flex flex-col items-center justify-center py-10 gap-4">
          <div className="h-14 w-14 flex items-center justify-center rounded-full bg-green-500/10">
            <Check className="h-7 w-7 text-green-500" />
          </div>
          <div className="text-center">
            <p className="font-semibold text-[var(--color-text-primary)] text-lg">
              Menu imported successfully!
            </p>
            <p className="text-sm text-[var(--color-text-secondary)] mt-1">
              {importResult?.categoriesCreated ?? 0} new categor{(importResult?.categoriesCreated ?? 0) !== 1 ? 'ies' : 'y'},{' '}
              {importResult?.itemsCreated ?? 0} item{(importResult?.itemsCreated ?? 0) !== 1 ? 's' : ''},{' '}
              {importResult?.variantsCreated ?? 0} variant{(importResult?.variantsCreated ?? 0) !== 1 ? 's' : ''}, and{' '}
              {importResult?.modifiersCreated ?? 0} option{(importResult?.modifiersCreated ?? 0) !== 1 ? 's' : ''} added
            </p>
          </div>
          <Button onClick={handleClose}>Done</Button>
        </div>
      )}
    </Modal>
  )
}
